// src/pages/vs/BloombergTerminalPage.jsx
// /vs/bloomberg-terminal — highest-converting comparison page
import { useEffect } from 'react';

const S = {
  page: { background: '#0a0a0a', color: '#c9d1d9', fontFamily: "'DM Sans', system-ui, sans-serif", minHeight: '100vh' },
  nav: { padding: '20px 24px', borderBottom: '1px solid #1c2333' },
  navInner: { maxWidth: 1060, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  navLogo: { fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 800, fontSize: 18, color: '#c9d1d9', textDecoration: 'none' },
  navLink: { color: '#3ddc84', textDecoration: 'none', fontSize: 14 },

  hero: { padding: '80px 24px 64px', textAlign: 'center' },
  heroInner: { maxWidth: 820, margin: '0 auto' },
  badge: { display: 'inline-block', background: '#0E180F', color: '#3ddc84', border: '1px solid #2db870', borderRadius: 4, padding: '4px 12px', fontSize: 11, fontFamily: "'IBM Plex Mono', 'Courier New', monospace", letterSpacing: 1, marginBottom: 24 },
  h1: { fontSize: 'clamp(32px, 5vw, 54px)', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 800, color: '#c9d1d9', margin: '0 0 24px', lineHeight: 1.1 },
  heroSub: { fontSize: 18, color: '#8b949e', marginBottom: 48, lineHeight: 1.6, maxWidth: 640, margin: '0 auto 48px' },
  ctaRow: { display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' },
  ctaPrimary: { display: 'inline-block', padding: '14px 32px', background: '#3ddc84', color: '#0a0a0a', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 16, borderRadius: 8, textDecoration: 'none' },
  ctaSecondary: { display: 'inline-block', padding: '14px 32px', background: 'transparent', color: '#3ddc84', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 16, borderRadius: 8, textDecoration: 'none', border: '1px solid #2db870' },

  priceBar: { background: '#080c12', borderTop: '1px solid #1c2333', borderBottom: '1px solid #1c2333', padding: '40px 24px' },
  priceBarInner: { maxWidth: 1060, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 0 },
  priceItem: { textAlign: 'center', padding: '16px 24px', borderRight: '1px solid #1c2333' },
  priceItemLast: { textAlign: 'center', padding: '16px 24px' },
  priceLabel: { fontFamily: "'IBM Plex Mono', 'Courier New', monospace", fontSize: 11, color: '#8b949e', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 },
  priceValue: { fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 800, fontSize: 28, color: '#c9d1d9' },
  priceNote: { fontSize: 12, color: '#8b949e', marginTop: 4 },

  section: { padding: '80px 24px' },
  sectionAlt: { padding: '80px 24px', background: '#080c12' },
  inner: { maxWidth: 1060, margin: '0 auto' },
  sectionLabel: { fontFamily: "'IBM Plex Mono', 'Courier New', monospace", fontSize: 11, color: '#3ddc84', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 },
  h2: { fontSize: 'clamp(24px, 4vw, 40px)', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 800, color: '#c9d1d9', margin: '0 0 16px', lineHeight: 1.2 },
  sectionIntro: { fontSize: 16, color: '#8b949e', marginBottom: 48, maxWidth: 680, lineHeight: 1.7 },

  // Comparison table
  tableWrap: { overflowX: 'auto', border: '1px solid #1c2333', borderRadius: 12 },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: '#111111' },
  th: { padding: '16px 20px', textAlign: 'left', fontSize: 12, color: '#8b949e', fontFamily: "'IBM Plex Mono', 'Courier New', monospace", letterSpacing: 1, textTransform: 'uppercase', borderBottom: '1px solid #1c2333' },
  thProduct: { padding: '16px 20px', textAlign: 'center', fontSize: 13, fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, borderBottom: '1px solid #1c2333' },
  td: { padding: '14px 20px', fontSize: 14, borderBottom: '1px solid #1c2333', color: '#8b949e' },
  tdCheck: { padding: '14px 20px', textAlign: 'center', fontSize: 14, borderBottom: '1px solid #1c2333' },
  checkYes: { color: '#3ddc84', fontWeight: 700 },
  checkNo: { color: '#444d56' },
  rowAlt: { background: '#0a0a0a' },

  grid3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 },
  card: { background: '#111111', border: '1px solid #1c2333', borderRadius: 12, padding: 32 },
  cardAccent: { background: '#111111', border: '1px solid #3ddc84', borderRadius: 12, padding: 32 },
  cardTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 18, color: '#c9d1d9', marginBottom: 12 },
  cardBody: { fontSize: 14, color: '#8b949e', lineHeight: 1.7 },

  moduleGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 },
  moduleItem: { background: '#0d1117', border: '1px solid #1c2333', borderRadius: 8, padding: '14px 18px', display: 'flex', gap: 10, alignItems: 'flex-start' },
  moduleDot: { color: '#3ddc84', fontWeight: 700, flexShrink: 0, marginTop: 2 },
  moduleText: { fontSize: 13, color: '#c9d1d9' },

  faqItem: { borderBottom: '1px solid #1c2333', paddingBottom: 28, marginBottom: 28 },
  faqQ: { fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 17, color: '#c9d1d9', marginBottom: 12 },
  faqA: { fontSize: 14, color: '#8b949e', lineHeight: 1.8 },

  ctaSection: { padding: '100px 24px', textAlign: 'center', background: '#080c12' },
  ctaH2: { fontSize: 'clamp(28px, 4vw, 44px)', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 800, color: '#c9d1d9', margin: '0 auto 20px', maxWidth: 700 },
  ctaBody: { fontSize: 16, color: '#8b949e', marginBottom: 40, maxWidth: 540, margin: '0 auto 40px' },
  ctaNote: { fontSize: 13, color: '#8b949e', marginTop: 20 },
};

const COMPARISON_ROWS = [
  { feature: 'Monthly cost', bloomberg: '$2,000/month', nuvport: '$29/month', bloombergHas: null, nuvportHas: null },
  { feature: 'Annual cost', bloomberg: '$24,000/year', nuvport: '$348/year', bloombergHas: null, nuvportHas: null },
  { feature: 'Free tier', bloomberg: '—', nuvport: 'Free forever, no card', bloombergHas: false, nuvportHas: true },
  { feature: 'Portfolio geo-risk map', bloomberg: null, nuvport: null, bloombergHas: true, nuvportHas: true },
  { feature: 'Live AIS tanker tracking', bloomberg: null, nuvport: null, bloombergHas: true, nuvportHas: true },
  { feature: 'DCF valuation model', bloomberg: null, nuvport: null, bloombergHas: true, nuvportHas: true },
  { feature: 'Portfolio optimizer', bloomberg: null, nuvport: null, bloombergHas: true, nuvportHas: true },
  { feature: 'Monte Carlo simulation', bloomberg: null, nuvport: null, bloombergHas: true, nuvportHas: true },
  { feature: 'Portfolio backtesting', bloomberg: null, nuvport: null, bloombergHas: true, nuvportHas: true },
  { feature: 'Insider intelligence (SEC Form 4)', bloomberg: null, nuvport: null, bloombergHas: true, nuvportHas: true },
  { feature: 'Stock screener', bloomberg: null, nuvport: null, bloombergHas: true, nuvportHas: true },
  { feature: 'Fixed income & yield curves', bloomberg: null, nuvport: null, bloombergHas: true, nuvportHas: true },
  { feature: 'Macro dashboard (FRED data)', bloomberg: null, nuvport: null, bloombergHas: true, nuvportHas: true },
  { feature: 'FX markets — 47 currency pairs', bloomberg: null, nuvport: null, bloombergHas: true, nuvportHas: true },
  { feature: 'AI-scored news sentiment', bloomberg: null, nuvport: null, bloombergHas: true, nuvportHas: true },
  { feature: 'Designed for retail investors', bloomberg: null, nuvport: null, bloombergHas: false, nuvportHas: true },
  { feature: 'No Bloomberg sales process', bloomberg: null, nuvport: null, bloombergHas: false, nuvportHas: true },
  { feature: 'Instant web access', bloomberg: null, nuvport: null, bloombergHas: false, nuvportHas: true },
];

const MODULES = [
  'Portfolio Dashboard', 'Portfolio Builder', 'Valuation Screen', 'Portfolio Optimizer',
  'DCF Valuation Model', 'Monte Carlo Simulation', 'Portfolio Backtesting', 'Charts & Analytics',
  'Geo-Risk Map', 'News Sentiment (AI)', 'Stock Screener', 'Market Watch',
  'Macro Dashboard', 'FX & Markets', 'Fixed Income & Yield Curves', 'Insider Intelligence',
  'World Monitor (AIS)', 'Earnings Calendar', 'Commodities', 'FX Rates',
  'Economic Indicators',
];

const FAQS = [
  {
    q: 'Is Nuvport OS really a Bloomberg Terminal alternative?',
    a: 'For retail investors, yes. Bloomberg Terminal is built for institutional trading desks — fixed income traders, equity research analysts, risk managers at asset managers and hedge funds. It provides deep institutional data, chat, and analytics that most retail investors will never use. Nuvport OS is built for individual investors who want institutional-quality analysis tools — DCF modeling, portfolio optimization, geo-risk mapping, insider intelligence — without the $24,000/year price tag or the Bloomberg sales process. The tools overlap significantly. The price and the target user do not.',
  },
  {
    q: 'What does Bloomberg Terminal actually cost?',
    a: 'Bloomberg Terminal is priced at approximately $2,000 per month per terminal, or roughly $24,000 per year. That is per seat — if you are at a firm with multiple users, costs multiply accordingly. The pricing requires a direct subscription through Bloomberg LP and typically involves a minimum commitment period. Bloomberg does not publish pricing publicly; the figure above is widely cited in financial industry sources and reflects standard retail subscriber pricing as of early 2026.',
  },
  {
    q: 'What does Nuvport OS not have that Bloomberg does?',
    a: 'Bloomberg Terminal has capabilities that Nuvport OS does not offer and does not claim to replace: Bloomberg Chat (the messaging network used by institutional traders), real-time bond pricing and fixed income execution, foreign exchange execution, institutional-grade derivatives data, and the full depth of Bloomberg\'s proprietary data terminals and indices. If you are a fixed income trader, a sell-side analyst, or a risk manager at an institutional firm, Bloomberg Terminal is built for your workflow. Nuvport OS is not. Nuvport OS is built for retail investors who want institutional-grade analysis tools — not institutional trade execution infrastructure.',
  },
  {
    q: 'How long does it take to set up Nuvport OS?',
    a: 'Under two minutes. Sign up, add your holdings, and every module is immediately available. There is no Bloomberg hardware terminal to provision, no sales call to schedule, no contract to sign. The free tier is available instantly. Pro activates the moment you subscribe.',
  },
  {
    q: 'Is there a free version of Nuvport OS?',
    a: 'Yes. The free tier includes the Portfolio Dashboard, Portfolio Builder (up to 5 holdings), and live Watchlist prices — permanently free with no expiry. The Pro plan at $29/month unlocks all 21 modules, unlimited holdings, and the full Geo-Risk Map. Start with the free tier and upgrade whenever you are ready.',
  },
];

export default function BloombergTerminalPage() {
  useEffect(() => {
    document.title = 'Nuvport OS vs Bloomberg Terminal — $29/month vs $2,000/month';
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      'Compare Nuvport OS vs Bloomberg Terminal. Get institutional-grade investment tools — portfolio optimizer, DCF model, geo-risk map, insider intelligence — at $29/month vs Bloomberg\'s $2,000/month.'
    );
  }, []);

  return (
    <div style={S.page}>
      {/* Nav */}
      <nav style={S.nav}>
        <div style={S.navInner}>
          <a href="/" style={S.navLogo}>Nuvport OS</a>
          <a href="/" style={S.navLink}>← Back to terminal</a>
        </div>
      </nav>

      {/* Hero */}
      <section style={S.hero}>
        <div style={S.heroInner}>
          <div style={S.badge}>BLOOMBERG ALTERNATIVE</div>
          <h1 style={S.h1}>Bloomberg Terminal costs $2,000/month.<br />Nuvport OS costs $29.</h1>
          <p style={S.heroSub}>
            Get the institutional-grade investment intelligence retail investors actually need — portfolio optimization,
            DCF modeling, geo-risk mapping, insider intelligence, and more — at 98.5% lower cost than Bloomberg Terminal.
          </p>
          <div style={S.ctaRow}>
            <a href="/signup?ref=vs-bloomberg" style={S.ctaPrimary}>Start Free — No Credit Card →</a>
            <a href="#comparison" style={S.ctaSecondary}>See full comparison</a>
          </div>
        </div>
      </section>

      {/* Price bar */}
      <div style={S.priceBar}>
        <div style={S.priceBarInner}>
          <div style={S.priceItem}>
            <div style={S.priceLabel}>Bloomberg Terminal</div>
            <div style={{ ...S.priceValue, color: '#8b949e' }}>$2,000<span style={{ fontSize: 16, fontWeight: 400 }}>/mo</span></div>
            <div style={S.priceNote}>$24,000/year per seat</div>
          </div>
          <div style={S.priceItem}>
            <div style={S.priceLabel}>Nuvport OS Pro</div>
            <div style={{ ...S.priceValue, color: '#3ddc84' }}>$29<span style={{ fontSize: 16, fontWeight: 400 }}>/mo</span></div>
            <div style={S.priceNote}>$348/year · Cancel anytime</div>
          </div>
          <div style={S.priceItem}>
            <div style={S.priceLabel}>Annual savings</div>
            <div style={{ ...S.priceValue, color: '#3ddc84' }}>$23,652</div>
            <div style={S.priceNote}>Per user per year</div>
          </div>
          <div style={S.priceItemLast}>
            <div style={S.priceLabel}>Cost reduction</div>
            <div style={{ ...S.priceValue, color: '#3ddc84' }}>98.5%</div>
            <div style={S.priceNote}>Less than Bloomberg</div>
          </div>
        </div>
      </div>

      {/* Why Bloomberg is wrong for retail */}
      <section style={S.section}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>The Problem</div>
          <h2 style={S.h2}>Bloomberg Terminal was never built for you.</h2>
          <p style={S.sectionIntro}>Bloomberg Terminal is institutional infrastructure. It was designed for the trading desk, not the individual investor. Understanding what it is — and what it is not — is the starting point for every comparison.</p>
          <div style={S.grid3}>
            <div style={S.card}>
              <div style={S.cardTitle}>It was built for institutions.</div>
              <div style={S.cardBody}>Bloomberg Terminal's design reflects decades of serving fixed income traders, sell-side analysts, and asset management teams. The interface, the data depth, and the pricing structure all assume you are operating inside an institutional firm — not managing your own portfolio.</div>
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>$24,000/year creates a moat by design.</div>
              <div style={S.cardBody}>The price point is not accidental. It ensures that institutional-grade intelligence stays institutional. Retail investors have historically been given simplified tools, simplified data, and simplified analysis — not because better tools don't exist, but because they were priced out of reach.</div>
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>Most of what Bloomberg offers, you won't use.</div>
              <div style={S.cardBody}>Bloomberg Chat. Bond execution. Derivatives pricing. FX order management. These are the features that justify $2,000/month for an institutional desk. As a retail investor managing your own portfolio, the tools that matter are analysis, modelling, and intelligence — not execution infrastructure.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature comparison table */}
      <section id="comparison" style={S.sectionAlt}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>Feature Comparison</div>
          <h2 style={S.h2}>Nuvport OS vs Bloomberg Terminal — side by side.</h2>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead style={S.thead}>
                <tr>
                  <th style={S.th}>Feature</th>
                  <th style={{ ...S.thProduct, color: '#8b949e' }}>Bloomberg Terminal</th>
                  <th style={{ ...S.thProduct, color: '#3ddc84' }}>Nuvport OS</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map(({ feature, bloomberg, nuvport, bloombergHas, nuvportHas }, i) => (
                  <tr key={feature} style={i % 2 === 0 ? {} : { background: '#080c12' }}>
                    <td style={S.td}>{feature}</td>
                    <td style={S.tdCheck}>
                      {bloomberg !== null
                        ? <span style={{ color: '#8b949e' }}>{bloomberg}</span>
                        : bloombergHas
                          ? <span style={S.checkYes}>✓</span>
                          : <span style={S.checkNo}>—</span>
                      }
                    </td>
                    <td style={S.tdCheck}>
                      {nuvport !== null
                        ? <span style={{ color: '#3ddc84' }}>{nuvport}</span>
                        : nuvportHas
                          ? <span style={S.checkYes}>✓</span>
                          : <span style={S.checkNo}>—</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 21 modules */}
      <section style={S.section}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>What You Get</div>
          <h2 style={S.h2}>21 modules. One subscription. No Bloomberg prices.</h2>
          <p style={S.sectionIntro}>Every Pro module is included in the $29/month plan. No add-ons, no data tiers, no per-module pricing.</p>
          <div style={S.moduleGrid}>
            {MODULES.map(m => (
              <div key={m} style={S.moduleItem}>
                <span style={S.moduleDot}>✓</span>
                <span style={S.moduleText}>{m}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The one thing Bloomberg doesn't have */}
      <section style={S.sectionAlt}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>The Differentiator</div>
          <h2 style={S.h2}>The one thing Bloomberg has. The one thing it doesn't.</h2>
          <p style={S.sectionIntro}>Bloomberg has geo-risk data. Bloomberg has AIS terminals. Neither is designed to map your specific retail portfolio to live conflict zones.</p>
          <div style={S.grid3}>
            <div style={S.card}>
              <div style={S.cardTitle}>Bloomberg has institutional geo-risk data.</div>
              <div style={S.cardBody}>Bloomberg Terminal provides access to geopolitical risk analytics through its data services. It is deep, accurate, and expensive. It is also designed for a risk analyst at an asset manager running a $500M book — not for a retail investor managing their own ISA or brokerage account.</div>
            </div>
            <div style={{ ...S.cardAccent }}>
              <div style={S.cardTitle}>Nuvport OS has the world's only retail portfolio geo-risk map.</div>
              <div style={S.cardBody}>19 live conflict zones. Portfolio exposure overlay. AIS tanker tracking. 14-event live alert bus. A composite Geo-Risk Score from 0 to 100. All mapped to your specific holdings — not to a generic sector index. This does not exist anywhere else at a retail price point. Including Bloomberg.</div>
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>The gap is closing. The price isn't.</div>
              <div style={S.cardBody}>Bloomberg's institutional data advantage has narrowed as public data sources have improved. What has not changed: the price, the sales process, and the assumption that serious investment tools are only for serious institutional money. Nuvport OS disagrees with that assumption.</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={S.section}>
        <div style={S.inner}>
          <div style={S.sectionLabel}>FAQ</div>
          <h2 style={S.h2}>Frequently asked questions.</h2>
          <div>
            {FAQS.map(({ q, a }) => (
              <div key={q} style={S.faqItem}>
                <div style={S.faqQ}>{q}</div>
                <div style={S.faqA}>{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={S.ctaSection}>
        <h2 style={S.ctaH2}>Bloomberg intelligence. Not Bloomberg prices.</h2>
        <p style={S.ctaBody}>Start free today. No credit card required. Full Pro access from day one — all 21 modules, the geo-risk map, insider intelligence, and live event monitoring.</p>
        <div style={S.ctaRow}>
          <a href="/signup?ref=vs-bloomberg" style={S.ctaPrimary}>Start Free</a>
          <a href="/pricing" style={S.ctaSecondary}>See pricing →</a>
        </div>
        <p style={S.ctaNote}>$29/month for Pro. Cancel any time. No Bloomberg sales call required.</p>
      </section>
    </div>
  );
}
